import type { ClientPlaygroundConfigOptions, MockClient, MockEnvironment } from '@gotti/test-utils';
import { MOCK_GAME_ID, initializer, installFocusScopedInput, registerClientCreationHook, scopeProcessInputToClient } from '@gotti/test-utils';
import { inputTokenOf, listClients } from './clients.js';
import { createLayoutDomSetup } from './layout.js';
import { refuseContradictoryOffline } from '../config/offline.js';
import { isPlaygroundPair, resolvePairVariant } from '../config/pair.js';
import type { PlaygroundPairModule } from '../config/pair.js';
import {
  leafConfigView,
  playgroundEntries,
  resolvePlaygroundModule,
  selectPlaygroundRuntime,
} from '../config/shared.js';
import { applyPlaygroundGameHooks } from './gameHooks.js';
import type {
  PlaygroundConfig,
  PlaygroundModule,
  PlaygroundDefinitionModule,
  PlaygroundMode,
  PlaygroundRuntimeSelection,
  PlaygroundSetupContext,
  PlaygroundSetupHandler,
} from '../types.js';
import { setCurrentPlaygroundContext } from './context.js';

/** The inline entry `member` names in the config's `playgrounds`, by the node loader's own rules (`playgroundEntries`). */
const inlineMember = (config: PlaygroundConfig, member: string): unknown => {
  for (const declared of playgroundEntries(config)) {
    if ('name' in declared && declared.name === member) return declared.entry;
  }
  throw new Error(`[gotti][playground] Playground "${member}" is not an inline entry of the config's playgrounds.`);
};

export const resolveTarget = (target?: string | HTMLElement): HTMLElement => {
  if (target instanceof HTMLElement) {
    return target;
  }
  if (typeof target === 'string') {
    const el = document.querySelector<HTMLElement>(target);
    if (el) return el;
  }
  let existing = document.getElementById('gotti-playground-root');
  if (!existing) {
    existing = document.createElement('div');
    existing.id = 'gotti-playground-root';
    existing.style.width = '100%';
    existing.style.height = '100%';
    document.body.appendChild(existing);
  }
  return existing;
};

/**
 * Scopes every keyboard listener `client`'s systems register to that client, under its own token. Registered
 * as a test-utils client creation hook, so it runs as each client is constructed — before it authenticates or
 * joins — whoever creates it: the default setup, `initClients`, a custom `setup`'s own `initializer(...)`, a step.
 */
const scopeClientInputAtCreation = (client: MockClient): void => scopeProcessInputToClient(client.process, inputTokenOf(client));

const runDefaultSetup = async (
  selection: PlaygroundRuntimeSelection,
  target: HTMLElement,
  ctx: PlaygroundSetupContext
): Promise<MockEnvironment | null> => {
  const envDef = selection.environment;
  // The definition's `joinGame` wins over the config's, as `createClients` layers them.
  const declaredJoin = selection.clientConfig.joinGame ?? selection.clientConfigDefaults?.joinGame;
  // An offline boot already lists its client area files, under `offline.initAreas`: an omitted `clientAreas`
  // is those files. The initializer registers every client area file with every client, so it needs a list.
  const clientAreas = envDef?.clientAreas ?? envDef?.offline?.initAreas.map(({ areaFile }) => areaFile);
  // An environment that declares none of the four fields the boot needs — `environment: {}`, the project template's
  // placeholder, or one holding only `assets` (as a host layers its asset seam onto every definition) — boots none, as an
  // omitted one does. One that declares some must declare them all: a missing one is refused by name, never skipped — a
  // skip would leave every step failing on `ctx.env` with advice to declare the environment it declared.
  if (!envDef || [envDef.clientGame, envDef.serverGame, clientAreas, envDef.serverAreas].every((field) => field === undefined)) {
    console.warn('[gotti][playground] Missing environment configuration; skipping default setup.');
    return null;
  }
  if (!envDef.clientGame || !envDef.serverGame || !clientAreas || !envDef.serverAreas) {
    const missing = [
      ...(envDef.clientGame ? [] : ['clientGame']),
      ...(envDef.serverGame ? [] : ['serverGame']),
      ...(clientAreas ? [] : ['clientAreas']),
      ...(envDef.serverAreas ? [] : ['serverAreas']),
    ];
    throw new Error(
      `[gotti][playground] environment is missing ${missing.join(', ')}: the default setup boots clientGame, serverGame, ` +
        `clientAreas and serverAreas together — declare ${missing.length > 1 ? 'them' : 'it'}, or no environment at all.`,
    );
  }

  // The definition's clientConfig layered over the config's, through the initializer's own
  // base/override merge (`clientAuth({ baseAuth })`, `domSetup`, `onClientCreated`), exactly as a
  // vitest caller of `createClients(count, options, base)` gets it. One object declared at both
  // levels is one layer, not two.
  const clientConfig: ClientPlaygroundConfigOptions = { ...selection.clientConfig };
  let defaults = selection.clientConfigDefaults !== selection.clientConfig ? selection.clientConfigDefaults : undefined;
  const count = clientConfig.numberOfClients ?? defaults?.numberOfClients ?? 1;
  // The shape of a 'grid' cell: the definition's aspectRatio wins over the config's, like every other field.
  const aspectRatio = clientConfig.aspectRatio ?? defaults?.aspectRatio;
  // Where the canvases come from — the most specific declaration wins: the definition's `domSetup`,
  // then its `layout`, then the config's `domSetup`, then the config's `layout`, then the default
  // for the client count ('grid' for more than one client, else 'single').
  if (!clientConfig.domSetup) {
    if (clientConfig.layout) {
      clientConfig.domSetup = createLayoutDomSetup(target, count, clientConfig.layout, aspectRatio);
      // A layered config `domSetup` would still run for every client; the definition's layout outranks it.
      if (defaults?.domSetup) {
        const { domSetup: _outranked, ...rest } = defaults;
        defaults = rest;
      }
    } else if (!defaults?.domSetup) {
      clientConfig.domSetup = createLayoutDomSetup(target, count, defaults?.layout ?? (count > 1 ? 'grid' : 'single'), aspectRatio);
    }
  }

  const offline = envDef.offline;
  if (offline) {
    // The initializer's own join would ask the mock gate for areas; offline joins with areas suppressed
    // below. Stripped from BOTH layers — a config-level `joinGame` would still join — on the copies,
    // never on the selection's own objects.
    delete clientConfig.joinGame;
    if (defaults?.joinGame) {
      const { joinGame: _joinedOffline, ...rest } = defaults;
      defaults = rest;
    }
  }

  // Every client this creates — `initClients` inside `initializer`, then `createClients` — is scoped by the
  // creation hook `bootPlaygroundSelection` registered before `setup` ran.
  const environment = await initializer(
    {
      clientGame: envDef.clientGame,
      serverGame: envDef.serverGame,
      clientArea: clientAreas,
      serverArea: envDef.serverAreas,
      gate: envDef.gate,
      master: envDef.master,
      initClients: envDef.initClients,
      assets: envDef.assets,
    },
    selection.mockConfig
  );

  const created = await environment.createClients(count, clientConfig, defaults);
  if (offline) {
    // Every client joins the game with its area joins suppressed — the join still goes to the gate (the
    // environment's own, or the mock's) for the game's data — then starts every listed client area itself.
    // The clients `createClients` made take the declared body exactly as online hands it to them: numbered
    // within that call, with the payload each authenticated with. Any other client (an `initClients` one)
    // joins the bare game: online it would not join at all, but offline the boot is its join declaration.
    const gameId = declaredJoin?.gameId ?? envDef.serverGame.id ?? MOCK_GAME_ID;
    for (const client of listClients(environment)) {
      const index = created.indexOf(client);
      const body = index === -1
        ? undefined
        : typeof declaredJoin?.options === 'function' ? declaredJoin.options({ index, auth: client.authPayload }) : declaredJoin?.options;
      await client.joinGame(body ? { gameId, ...body } : gameId, undefined, /* suppressJoinAreas */ true);
      for (const { areaFile, id } of offline.initAreas) {
        await client.process.initAreaFile(areaFile, id);
      }
    }
  }
  ctx.environment = environment;
  return environment;
};

/** What `resolvePlaygroundSelection` takes: the evaluated config and definition modules and which playground they are. */
export interface ResolvePlaygroundOptions {
  module: PlaygroundModule | { default?: PlaygroundModule };
  definition?: PlaygroundDefinitionModule | PlaygroundPairModule<any, any, any> | { default?: PlaygroundDefinitionModule | PlaygroundPairModule<any, any, any> };
  variant?: PlaygroundMode;
  member?: string;
  setup?: PlaygroundSetupHandler;
  playgroundName: string;
}

/**
 * Resolves the config and definition modules — an inline `member`, a function definition, a pair's half — into the
 * selection the page runs. Throws when no definition is provided, or a definition key is unknown.
 */
export const resolvePlaygroundSelection = async (options: ResolvePlaygroundOptions): Promise<PlaygroundRuntimeSelection> => {
  const moduleValue = (options.module as any)?.default ?? (options.module as PlaygroundModule);
  const resolvedConfig = await resolvePlaygroundModule(moduleValue);
  // A lone definition (or pair) file served as its own config lists no playgrounds: the same view the node loader
  // builds of it, its one entry the module itself under the member's name.
  const baseConfig = options.member !== undefined && !resolvedConfig.playgrounds ? leafConfigView(resolvedConfig, options.member) : resolvedConfig;

  let definition: any = options.member !== undefined
    ? inlineMember(baseConfig, options.member)
    : (options.definition as any)?.default ?? (options.definition as PlaygroundDefinitionModule);
  if (definition && typeof definition === 'function') {
    definition = await definition({ command: 'serve', mode: 'development' });
  }
  if (definition && typeof definition === 'object' && 'default' in definition) {
    definition = definition.default;
  }
  if (isPlaygroundPair(definition)) {
    definition = await resolvePairVariant(definition, options.variant ?? 'online', { config: baseConfig });
  }

  if (!definition) {
    // Fallback: try to find definition in baseConfig if possible (unlikely in browser without globs)
    // Or maybe baseConfig IS the definition if resolvePlaygroundModule was confused?
    // But resolvePlaygroundModule returns PlaygroundConfig.
    throw new Error('Playground definition not provided to bootstrap.');
  }

  const selection = selectPlaygroundRuntime(baseConfig, definition, options.playgroundName, '');

  // Apply overrides from options (e.g. imported from separate files)
  if (options.setup) {
    // If options.setup is provided, it overrides selection.setup
    // But wait, options.setup might be the default export of a module if imported via `import setupFn from ...`
    // If the file exports default function, it's fine.
    // If it exports named function, we might need to handle that.
    // Assuming default export for now as per generator template.
    const setupFn = (options.setup as any).default ?? options.setup;
    selection.setup = setupFn;
  }
  return selection;
};

/**
 * Boots a resolved selection into `target`: installs focus-scoped input and scopes every client created from here
 * on (the creation hook stays registered for the page's life, as it always has), runs the definition's `setup` or
 * the default environment build, applies the game hooks, and publishes the runtime context. Resolves to the
 * environment (`null` when the playground builds none). Throws what `setup` throws. A boot given up through `signal`
 * (`mountPlayground`'s host cancelled it) runs nothing more once it resumes — no default setup after a `setup` it
 * awaited, no game hooks, no runtime context — and resolves to what it built so far, for its host to stop.
 */
export const bootPlaygroundSelection = async (
  selection: PlaygroundRuntimeSelection,
  target: HTMLElement,
  options: { signal?: AbortSignal } = {},
): Promise<MockEnvironment | null> => {
  console.info('[gotti][playground][test] bootstrap selection loaded', {
    playground: selection.name,
    hasSetup: typeof selection.setup === 'function',
    hasEnvironment: !!selection.environment,
  });

  // Every in-page client listens on the same `window`. Before `setup` runs — whatever builds the environment —
  // the router is installed and every client test-utils creates from here on is scoped at its construction, so
  // `ctx.focus` can route a key to one client. Registering the same hook again (a second bootstrap in the page)
  // is a no-op.
  installFocusScopedInput();
  registerClientCreationHook(scopeClientInputAtCreation);

  // A declared offline boot that contradicts itself is refused before anything runs — a `setup` that builds its own
  // environment included — with the messages node selection refuses it with: the same on every entry point. The
  // definition's `joinGame` wins over the config's, as `createClients` layers them.
  if (selection.environment?.offline) {
    refuseContradictoryOffline(selection.environment, selection.clientConfig.joinGame ?? selection.clientConfigDefaults?.joinGame);
  }

  const context: PlaygroundSetupContext = {
    definition: selection.definition,
    target: target,
    environment: null,
    runDefaultSetup: async () => null,
  };

  context.runDefaultSetup = async () => {
    const env = await runDefaultSetup(selection, target, context);
    applyPlaygroundGameHooks(selection, env);
    return env;
  };

  if (selection.setup) {
    if (typeof selection.setup === 'function') {
      console.info('[gotti][playground] invoking selection.setup for playground', selection.name);
      await selection.setup(context);
      if (options.signal?.aborted) return context.environment ?? null;
      if (!context.environment && selection.environment) {
        await context.runDefaultSetup();
      } else {
        applyPlaygroundGameHooks(selection, context.environment ?? null);
      }
    } else {
      console.warn('[gotti][playground] setup is defined as a string path but was not resolved to a function. Check generator logic.', selection.setup);
      // Fallback to default setup if possible, or just warn?
      // If setup was intended but failed to load, we probably shouldn't run default setup blindly if it depended on setup.
      // But if it's just a path that wasn't loaded, maybe we can still run environment?
      if (selection.environment) {
        await context.runDefaultSetup();
      }
    }
  } else if (selection.environment) {
    await context.runDefaultSetup();
  } else {
    console.warn(`[gotti][playground] Playground "${selection.name}" has no setup or environment configuration.`);
  }

  if (options.signal?.aborted) return context.environment ?? null;
  applyPlaygroundGameHooks(selection, context.environment ?? null);

  setCurrentPlaygroundContext({
    selection,
    environment: context.environment ?? null,
    target: target,
  });

  return context.environment ?? null;
};
